/**
 * /tenants/{slug}/automation-accounts — tenant-scoped list route (primary mount).
 *
 * URL-driven show_inactive toggle (per CLAUDE.md URL-state rule).
 * Uses the tenant UUID resolved by the parent layout (see
 * routes/_authed/tenants/$slug.tsx) so the backend call goes to
 * /api/v1/tenants/{tenant_id}/automation-accounts with the right
 * UUID, not the slug.
 */

import { AdminsTabs } from "@/features/platform-admins/components/admins-tabs";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  fetchAutomationAccounts,
  scopeKey,
} from "@/features/automation-accounts/queries";
import {
  automationAccountsListSearchSchema,
  type AutomationAccountsListSearch,
} from "@/features/automation-accounts/schemas";
import { queryKeys } from "@/lib/query-keys";
import { AutomationAccountsPage } from "@/features/automation-accounts/pages/automation-accounts-page";

function TenantAutomationAccountsPage() {
  const { tenant } = Route.useRouteContext();
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const showInactive = !!search.show_inactive;
  return (
    <AutomationAccountsPage
      tenantID={tenant.id}
      scope="tenant"
      title="Automation accounts"
      description={`Non-interactive admin identities scoped to this tenant — setup scripts, CI pipelines, and tenant-specific automation. Carry a tenant role; cannot act on other tenants. Distinct from ${tenant.name} users and from platform-level automation accounts.`}
      tabs={<AdminsTabs tenantSlug={slug} />}
      showInactive={showInactive}
      onToggleShowInactive={(next) =>
        void navigate({
          to: "/tenants/$slug/automation-accounts",
          params: { slug },
          search: next ? { show_inactive: true } : {},
        })
      }
      onNewAutomationAccount={() =>
        void navigate({
          to: "/tenants/$slug/automation-accounts/new",
          params: { slug },
        })
      }
      renderDetailLink={({ id, className, children, ...rest }) => (
        <Link
          to="/tenants/$slug/automation-accounts/$id"
          params={{ slug, id }}
          className={className}
          aria-label={rest["aria-label"]}
        >
          {children}
        </Link>
      )}
    />
  );
}

export const Route = createFileRoute(
  "/_authed/tenants/$slug/automation-accounts/",
)({
  validateSearch: (raw: Record<string, unknown>): AutomationAccountsListSearch =>
    automationAccountsListSearchSchema.parse(raw),
  loaderDeps: ({ search }) => ({ showInactive: !!search.show_inactive }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData({
      queryKey: [
        ...queryKeys.automationAccounts.list(scopeKey(context.tenant.id)),
        { showInactive: deps.showInactive },
      ] as const,
      queryFn: () =>
        fetchAutomationAccounts(context.tenant.id, {
          showInactive: deps.showInactive,
        }),
    }),
  component: TenantAutomationAccountsPage,
});
